import { useState } from 'react';
import type { ReactNode } from 'react';
import { useActions, useLive, useRuntime } from './context';
import { formatClock, formatMoney, weekdayNames } from '../mirror/time';
import { CatalogReadOnlyNote, OffWebsiteBadge, ProductsPanel } from './Products';
import { canChangeCatalog } from './seat';
import { ConciergePanel } from './Concierge';
import { ItemPhotos, ItemThumb } from './ItemPhotos';
import { useLocale, useT } from '../messages';
import type { T } from '../messages';
import type { DayHours } from '@yourstore/domain';
import { CatalogEditorsAnswer } from '../wire';
import type { ItemImage, Service } from '../wire';
import { buffersFor } from '@yourstore/scheduling';
import { groupByCategory } from './menu-groups';

/**
 * Settings — the menu, the week, and the days the shop is shut.
 *
 * The hours editor keeps `windows` as a **list** even though a spa's day is one
 * stretch, because the domain type is a list (spec §4.6: a restaurant opens
 * 11:30–14:00 and again 17:30–22:00) and an editor that only ever wrote one
 * window would silently truncate a split shift the moment the restaurant vertical
 * arrives. It is cheaper to render two rows now than to find out later that this
 * screen has been discarding somebody's evening service.
 *
 * Saving sends all seven days as one `PUT`, which is the shape `core-api`
 * insists on and for the offline reason: a week patched a day at a time by two
 * devices is a merge, and §8.3 does not merge.
 */
export function Settings(): ReactNode {
  const runtime = useRuntime();
  const session = useLive(() => runtime.mirror.session(), null);
  const currency = session?.tenant.currency ?? 'USD';
  const timezone = session?.tenant.timezone ?? 'UTC';
  const modules = session?.modules ?? [];

  return (
    <section className="view view--settings">
      <ServicesPanel currency={currency} />
      {/*
        Gated on the module, not hidden behind a preference. A panel for
        something the tenant has not bought would raise a `501` on its first
        write; capabilities are a projection of entitlements (spec §3.4), and so
        is this screen.
      */}
      {modules.includes('products') ? <ProductsPanel currency={currency} /> : null}
      {/*
        Gift cards are not here any more: they have their own tab (`App.tsx`,
        owner's decision 1 of 2026-09-23), because selling one is a sale with a
        guest at the desk and Settings is the screen visited rarely.
      */}
      {/*
        Gated **twice**, and the second gate is the interesting one. The module
        check is the ordinary §3.2 one — a tenant with no `partner_api` has no
        agent to tune. The role check is the owner line: `core-api` answers
        `403` to a `staff` or `readonly` seat on all four concierge routes
        (`routes/registry.ts`, `owner: true`), because an autonomy level is a
        standing instruction to a machine that answers the telephone, not an
        appointment a receptionist can undo. Drawing the panel for a seat that
        would be refused would be drawing a door that does not open.
      */}
      {modules.includes('partner_api') && session?.user.role === 'owner' ? (
        <ConciergePanel />
      ) : null}
      {/*
        Owner only, and hidden rather than disabled for everybody else: the
        route is `owner: true`, and "who may change what we sell" is not a
        question a front-desk seat gets to answer for itself (B10).
      */}
      {session !== null &&
      session.user.role === 'owner' &&
      session.can_write &&
      session.support === undefined ? (
        <CatalogEditorsPanel current={session.catalog_editors ?? 'owner'} />
      ) : null}
      <HoursPanel />
      <BlackoutsPanel timezone={timezone} />
      <SessionPanel />
    </section>
  );
}

/**
 * Every length she sells it at, with its price — "30 min $85 · 60 min $170" —
 * instead of only the headline length. A service with one way of being sold
 * reads as it always did.
 */
function lengthsLine(
  s: Service,
  currency: string,
  locale: ReturnType<typeof useLocale>,
  t: T,
): string {
  const live = (s.options ?? [])
    .filter((o) => o.active)
    .sort((a, b) => a.sort_order - b.sort_order || a.price_cents - b.price_cents);
  if (live.length < 2) {
    return t('settings.serviceLine', {
      minutes: s.duration_min,
      price: formatMoney(s.price_cents, currency, locale),
    });
  }
  return live
    .map((o) =>
      o.duration_min === null
        ? t('settings.optionNamed', {
            name: o.name,
            price: formatMoney(o.price_cents, currency, locale),
          })
        : t('settings.optionLength', {
            minutes: o.duration_min,
            price: formatMoney(o.price_cents, currency, locale),
          }),
    )
    .join(' · ');
}

function ServicesPanel({ currency }: { currency: string }): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const t = useT();
  const locale = useLocale();
  const services = useLive(() => runtime.mirror.services(), []);
  const resources = useLive(() => runtime.mirror.resources(), []);
  const session = useLive(() => runtime.mirror.session(), null);
  // The house's gap (A2), as the calendar reads it: the one resolver, so the
  // minutes printed here are the minutes the room is actually held for.
  const house = {
    default_buffer_before_min: session?.scheduling?.default_buffer_before_min ?? 0,
    default_buffer_after_min: session?.scheduling?.default_buffer_after_min ?? 0,
  };
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [duration, setDuration] = useState('60');
  const [editing, setEditing] = useState<string | null>(null);
  // B10: the menu's doors are drawn for whoever may change what the business
  // sells — `ProductsPanel`'s rule, one panel up.
  const canEdit = canChangeCatalog(session);
  const retired = services.filter((s) => !s.active);

  const renderRow = (s: Service): ReactNode => (
    <li key={s.service_id} className={s.active ? '' : 'is-retired'}>
      <div>
        <div className="item-row">
          <ItemThumb images={s.images} />
          <span className="item-row__text">
            <strong>{s.name}</strong>
            <small>
              {lengthsLine(s, currency, locale, t)}
              {bufferLine(s, house, t)}
            </small>
          </span>
        </div>
        {editing !== s.service_id ? null : canEdit ? (
          <ServiceEditor service={s} house={house} onDone={() => setEditing(null)} />
        ) : (
          <ItemPhotos mount="services" itemId={s.service_id} itemName={s.name} images={s.images} />
        )}
      </div>
      <div className="row-actions">
        {s.bookable_by_partner ? null : (
          <span className="badge" title={t('settings.counterOnlyTitle')}>
            {t('settings.counterOnly')}
          </span>
        )}
        {s.bookable_online ? null : <OffWebsiteBadge />}
        <button
          type="button"
          className="link-button"
          onClick={() => setEditing(editing === s.service_id ? null : s.service_id)}
        >
          {editing === s.service_id
            ? t('common.close')
            : canEdit
              ? t('common.edit')
              : t('catalog.details')}
        </button>
        {canEdit ? (
          <button
            type="button"
            className="link-button"
            onClick={() =>
              void actions.updateService({
                serviceId: s.service_id,
                name: s.name,
                changes: { active: !s.active },
              })
            }
          >
            {/*
              Retire, not delete. There is no `DELETE /v1/services` and there
              will not be: a service somebody was booked into last March is
              part of the book. `active: false` takes it off the menu and
              leaves the history intact (spec §7.2 — a switch, not a
              deletion).
            */}
            {s.active ? t('common.retire') : t('common.restore')}
          </button>
        ) : null}
      </div>
    </li>
  );

  return (
    <article className="panel">
      <h2>{t('settings.menu')}</h2>
      <CatalogReadOnlyNote session={session} />
      {/*
        Her card, as she hands it to guests: her headings in her order
        (`category`, `sort_order` — the server's order), each length of a
        treatment on its one row, and the retired entries folded away at the
        bottom rather than greyed in among what she sells today.
      */}
      {groupByCategory(services.filter((s) => s.active)).map(([heading, rows]) => (
        <section key={heading ?? ''} className="service-group">
          {heading === null ? null : <h3 className="service-group__heading">{heading}</h3>}
          <ul className="service-list">{rows.map(renderRow)}</ul>
        </section>
      ))}
      {retired.length === 0 ? null : (
        <details className="service-retired">
          <summary>{t('settings.retiredCount', { count: retired.length })}</summary>
          <ul className="service-list">{retired.map(renderRow)}</ul>
        </details>
      )}

      {canEdit ? (
        <div className="editor editor--inline">
          <input
            placeholder={t('common.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            placeholder={t('common.price')}
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
          <input
            placeholder={t('settings.minutes')}
            inputMode="numeric"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          />
          <button
            type="button"
            className="button"
            disabled={name.trim() === '' || price.trim() === ''}
            onClick={() => {
              void actions.createService({
                name: name.trim(),
                // Money is integer cents everywhere (dev.md), so the dollars the
                // owner types are converted exactly once, here, at the edge.
                priceCents: Math.round(Number(price) * 100),
                durationMin: Number(duration),
                resourceIds: resources.filter((r) => r.active).map((r) => r.resource_id),
              });
              setName('');
              setPrice('');
            }}
          >
            {t('settings.addService')}
          </button>
        </div>
      ) : null}
    </article>
  );
}

/**
 * The gap, in words, beside a service.
 *
 * Resolved through `buffersFor` rather than summed from the row, because since
 * 0064 the row may say `null` — "the house decides" — and the honest number is
 * the one the room is held for. When the house's default is what decided, the
 * line says so, so an owner reading "+30 min" beside a treatment she never
 * typed a gap for knows where the thirty came from.
 */
function bufferLine(
  service: { buffer_before_min: number | null; buffer_after_min: number | null },
  house: { default_buffer_before_min: number; default_buffer_after_min: number },
  t: T,
): string {
  const resolved = buffersFor(house, service);
  const minutes = resolved.before_min + resolved.after_min;
  if (minutes === 0) return '';
  const fromHouse = service.buffer_before_min === null || service.buffer_after_min === null;
  return t('settings.buffer', { minutes }) + (fromHouse ? t('settings.bufferFromHouse') : '');
}

/**
 * Editing a service — name, price, length, the gap either side, and whether
 * the agent may sell it.
 *
 * The two gap boxes carry three states (0064): blank is "the house decides",
 * `0` is none, a number is the item's own. Blank is sent as `null`, which is
 * what hands a treatment back to the house default; the placeholder says what
 * the house's number is, so blank is never a mystery.
 *
 * Only the fields that changed are sent. `UpdateServiceBody` is a partial and
 * `core-api` treats `undefined` as "leave it alone", so a form that always sent
 * every field would turn "I fixed a typo in the name" into a write that also
 * re-asserts a price somebody else edited on the other iPad five minutes ago.
 * Sending the diff is the difference between an edit and an overwrite.
 */
function ServiceEditor({
  service,
  house,
  onDone,
}: {
  service: {
    service_id: string;
    name: string;
    price_cents: number;
    duration_min: number;
    buffer_before_min: number | null;
    buffer_after_min: number | null;
    bookable_by_partner: boolean;
    bookable_online: boolean;
    images?: readonly ItemImage[] | undefined;
  };
  house: { default_buffer_before_min: number; default_buffer_after_min: number };
  onDone: () => void;
}): ReactNode {
  const actions = useActions();
  const t = useT();
  const [name, setName] = useState(service.name);
  const [price, setPrice] = useState((service.price_cents / 100).toFixed(2));
  const [duration, setDuration] = useState(String(service.duration_min));
  const [before, setBefore] = useState(bufferBox(service.buffer_before_min));
  const [after, setAfter] = useState(bufferBox(service.buffer_after_min));
  const [partner, setPartner] = useState(service.bookable_by_partner);
  // "Show on the website" is `bookable_online` for a treatment (B10): the
  // guest site shows exactly what it can book, and there is no second switch.
  const [online, setOnline] = useState(service.bookable_online);

  return (
    <>
      <div className="editor editor--inline">
        <input value={name} onChange={(e) => setName(e.target.value)} />
        <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
        <input inputMode="numeric" value={duration} onChange={(e) => setDuration(e.target.value)} />
        <input
          inputMode="numeric"
          aria-label={t('settings.bufferBefore')}
          placeholder={t('settings.houseDefault', { minutes: house.default_buffer_before_min })}
          value={before}
          onChange={(e) => setBefore(e.target.value)}
        />
        <input
          inputMode="numeric"
          aria-label={t('settings.bufferAfter')}
          placeholder={t('settings.houseDefault', { minutes: house.default_buffer_after_min })}
          value={after}
          onChange={(e) => setAfter(e.target.value)}
        />
        <label className="checkbox">
          <input type="checkbox" checked={partner} onChange={(e) => setPartner(e.target.checked)} />
          <span>{t('settings.agentMayBook')}</span>
        </label>
        <label className="checkbox">
          <input type="checkbox" checked={online} onChange={(e) => setOnline(e.target.checked)} />
          <span>{t('catalog.showOnWebsite')}</span>
        </label>
        <button
          type="button"
          className="button"
          onClick={() => {
            const priceCents = Math.round(Number(price) * 100);
            const changes: Record<string, unknown> = {};
            if (name !== service.name) changes['name'] = name;
            if (priceCents !== service.price_cents) changes['price_cents'] = priceCents;
            if (Number(duration) !== service.duration_min) {
              changes['duration_min'] = Number(duration);
            }
            // Only when it changed, and `null` for a blank: the diff rule above,
            // and the third state the column gained in 0064.
            if (bufferWire(before) !== service.buffer_before_min) {
              changes['buffer_before_min'] = bufferWire(before);
            }
            if (bufferWire(after) !== service.buffer_after_min) {
              changes['buffer_after_min'] = bufferWire(after);
            }
            if (partner !== service.bookable_by_partner) changes['bookable_by_partner'] = partner;
            if (online !== service.bookable_online) changes['bookable_online'] = online;
            if (Object.keys(changes).length > 0) {
              void actions.updateService({
                serviceId: service.service_id,
                name: service.name,
                changes,
              });
            }
            onDone();
          }}
        >
          {t('common.save')}
        </button>
      </div>
      {/* The treatment's photographs — `ProductEditor`'s note applies (B7). */}
      <ItemPhotos
        mount="services"
        itemId={service.service_id}
        itemName={service.name}
        images={service.images}
      />
    </>
  );
}

/**
 * **Who may change what we sell** (B10) — the owner's own answer, `owner` or
 * `owner_and_staff`, and the same key the operator console sets.
 *
 * Online and immediate rather than through the outbox, `ConciergePanel`'s
 * reason: it is a standing decision about who may do what, and queueing it
 * behind a tunnel would leave a front-desk seat holding the old answer with no
 * way to know. The session is re-read on success, so every screen on this
 * device redraws its controls from the server's word.
 */
function CatalogEditorsPanel({ current }: { current: 'owner' | 'owner_and_staff' }): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const choose = async (value: 'owner' | 'owner_and_staff'): Promise<void> => {
    if (value === current) return;
    setBusy(true);
    setStatus(null);
    setFailure(null);
    try {
      await runtime.api.request({
        method: 'PUT',
        path: '/settings/catalog-editors',
        body: { catalog_editors: value },
        schema: CatalogEditorsAnswer,
      });
      await runtime.mirror.refreshSession();
      setStatus(t('catalogEditors.saved'));
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="panel">
      <h2>{t('catalogEditors.heading')}</h2>
      <p className="muted">{t('catalogEditors.hint')}</p>
      <div role="radiogroup" aria-label={t('catalogEditors.heading')}>
        {(['owner', 'owner_and_staff'] as const).map((value) => (
          <label key={value} className="checkbox">
            <input
              type="radio"
              name="catalog-editors"
              value={value}
              checked={current === value}
              disabled={busy}
              onChange={() => void choose(value)}
            />
            <span>
              {value === 'owner' ? t('catalogEditors.owner') : t('catalogEditors.ownerAndStaff')}
            </span>
          </label>
        ))}
      </div>
      <p className="muted">{t('catalogEditors.readonlyNote')}</p>
      {status === null ? null : (
        <p className="note" role="status">
          {status}
        </p>
      )}
      {failure === null ? null : (
        <p className="error" role="alert">
          {failure}
        </p>
      )}
    </article>
  );
}

/** A buffer column as a box: `null` is a blank, a number is its digits. */
function bufferBox(stored: number | null): string {
  return stored === null ? '' : String(stored);
}

/** …and back: a blank is `null` — "the house decides" — and `0` is `0`. */
function bufferWire(typed: string): number | null {
  const trimmed = typed.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

function HoursPanel(): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const t = useT();
  const locale = useLocale();
  // Monday-first weekday names out of `Intl`, matching `DayHours.weekday`.
  // This used to be a hardcoded English array; a second hardcoded array would
  // have been the same mistake in another language.
  const dayNames = weekdayNames(locale);
  const stored = useLive(() => runtime.mirror.hours(), null);
  const [draft, setDraft] = useState<DayHours[] | null>(null);

  const week = draft ?? stored?.hours ?? emptyWeek();

  const change = (weekday: number, apply: (day: DayHours) => DayHours): void => {
    setDraft(week.map((d) => (d.weekday === weekday ? apply(d) : d)));
  };

  return (
    <article className="panel">
      <h2>{t('settings.openingHours')}</h2>
      <ul className="hours-list">
        {week.map((day) => (
          <li key={day.weekday}>
            <span className="hours-list__name">{dayNames[day.weekday]}</span>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={day.closed}
                onChange={(e) => change(day.weekday, (d) => ({ ...d, closed: e.target.checked }))}
              />
              <span>{t('settings.closed')}</span>
            </label>
            {day.closed
              ? null
              : day.windows.map((window, index) => (
                  <span key={index} className="hours-list__window">
                    <input
                      type="time"
                      value={window.start}
                      onChange={(e) =>
                        change(day.weekday, (d) => ({
                          ...d,
                          windows: d.windows.map((w, i) =>
                            i === index ? { ...w, start: e.target.value } : w,
                          ),
                        }))
                      }
                    />
                    <input
                      type="time"
                      value={window.end}
                      onChange={(e) =>
                        change(day.weekday, (d) => ({
                          ...d,
                          windows: d.windows.map((w, i) =>
                            i === index ? { ...w, end: e.target.value } : w,
                          ),
                        }))
                      }
                    />
                    <button
                      type="button"
                      className="link-button link-button--danger"
                      onClick={() =>
                        change(day.weekday, (d) => ({
                          ...d,
                          windows: d.windows.filter((_, i) => i !== index),
                        }))
                      }
                    >
                      ×
                    </button>
                  </span>
                ))}
            {day.closed ? null : (
              <button
                type="button"
                className="link-button"
                onClick={() =>
                  change(day.weekday, (d) => ({
                    ...d,
                    windows: [...d.windows, { start: '09:00', end: '17:00' }],
                  }))
                }
              >
                {t('settings.addWindow')}
              </button>
            )}
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="button button--primary"
        disabled={draft === null}
        onClick={() => {
          void actions.setHours(week);
          setDraft(null);
        }}
      >
        {t('settings.saveWeek')}
      </button>
    </article>
  );
}

function BlackoutsPanel({ timezone }: { timezone: string }): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const t = useT();
  const locale = useLocale();
  const blackouts = useLive(() => runtime.mirror.blackouts(), []);
  const [reason, setReason] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  return (
    <article className="panel">
      <h2>{t('settings.closures')}</h2>
      <ul className="blackout-list">
        {blackouts.map((b) => (
          <li key={b.blackout_id} className="blackout-list__row">
            <div>
              <span>
                {t('settings.blackoutSpan', {
                  from: formatClock(b.start, timezone, locale),
                  to: formatClock(b.end, timezone, locale),
                })}
                {b.reason === null ? '' : t('settings.blackoutReason', { reason: b.reason })}
              </span>
              {/*
                What this closure runs into (S2). The shop's own closures collide
                with bookings exactly as a person's time off does — and rather
                more loudly, since a whole day shut may hold several — so they
                are named here too. **Nothing cancels them**: the two ways out
                are to move the appointment or to shorten the closure, and both
                are the owner's decision (spec §8.3).
              */}
              {(b.conflicts ?? []).length > 0 ? (
                <>
                  <p className="warning">
                    {t('team.conflictsHeading', { count: (b.conflicts ?? []).length })}
                  </p>
                  <ul className="task__conflicts">
                    {(b.conflicts ?? []).map((c) => (
                      <li key={c.appointment_id}>
                        {t('team.conflictLine', {
                          when: formatClock(c.start, timezone, locale),
                          who: c.client_name,
                          what: c.service_name,
                        })}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>
            <button
              type="button"
              className="link-button link-button--danger"
              onClick={() =>
                void actions.deleteBlackout({ blackoutId: b.blackout_id, reason: b.reason })
              }
            >
              {t('common.remove')}
            </button>
          </li>
        ))}
        {blackouts.length === 0 ? <li className="muted">{t('settings.nothingClosed')}</li> : null}
      </ul>
      <div className="editor editor--inline">
        <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
        <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
        <input
          placeholder={t('settings.reason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <button
          type="button"
          className="button"
          disabled={start === '' || end === ''}
          onClick={() => {
            void actions.createBlackout({
              startIso: new Date(start).toISOString(),
              endIso: new Date(end).toISOString(),
              reason: reason.trim() === '' ? undefined : reason.trim(),
            });
            setStart('');
            setEnd('');
            setReason('');
          }}
        >
          {t('settings.closeThisTime')}
        </button>
      </div>
    </article>
  );
}

/**
 * What this device is, and what it may do.
 *
 * `modules` and `can_write` come from `GET /v1/session`, which is a
 * **projection** of the tenant's entitlements crossed with the mounted route
 * table — never a list this app maintains. Showing it is how an owner finds out
 * why a button is missing without anyone having to guess.
 */
function SessionPanel(): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const session = useLive(() => runtime.mirror.session(), null);
  if (session === null) return null;

  return (
    <article className="panel">
      <h2>{t('settings.thisDevice')}</h2>
      <dl className="facts">
        <dt>{t('settings.business')}</dt>
        <dd>
          {session.tenant.name} ({session.tenant.tenant_id})
        </dd>
        <dt>{t('settings.timezone')}</dt>
        <dd>{session.tenant.timezone}</dd>
        {/*
          `status`, `role` and `modules` are the server's vocabulary, projected
          by `GET /v1/session` from the tenant's entitlements. They are printed
          exactly as they arrived: this panel exists so an owner can read what
          the *server* thinks, and a translated `modules` list would be a list
          of words nobody could quote back to support.
        */}
        <dt>{t('settings.status')}</dt>
        <dd>{session.tenant.status}</dd>
        <dt>{t('settings.signedInAs')}</dt>
        <dd>
          {session.user.uid} · {session.user.role}
        </dd>
        <dt>{t('settings.switchedOn')}</dt>
        <dd>{session.modules.join(', ')}</dd>
        <dt>{t('settings.mayChange')}</dt>
        <dd>{session.can_write ? t('settings.canWriteYes') : t('settings.canWriteNo')}</dd>
      </dl>
    </article>
  );
}

function emptyWeek(): DayHours[] {
  return Array.from({ length: 7 }, (_, weekday) => ({ weekday, closed: true, windows: [] }));
}
